import * as React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Button from '@mui/material/Button';
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import Modal from "@mui/material/Modal";
import { styled } from "@mui/material/styles";

import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import Skeleton from '@mui/material/Skeleton';

import { cardNewsImageUrl, useCardNews } from "../../utils/cardNews";

// 맞춤플랜 섹션 — 디지털트윈 화면 2 와 공용
import MainCarePlan from './MainCarePlan';


const Img = styled('img')({
  margin: 'auto',
  display: 'block',
  maxWidth: '100%',
  maxHeight: '100%',
});

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 20px)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  '& div :focus-visible': { outline: 0 },
  '& .slick-prev': {
    left: 0,
    zIndex: 99
  },
  '& .slick-next': {
    right: 10,
    zIndex: 99
  },
  '& .slick-prev:before, .slick-next:before': {
    fontSize: 30,
    color: '#999'
  },

};

/** 이 영역에 노출하는 카드 수 */
const NEWS_COUNT = 5;

const MainCare = () => {
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);
  const [imageList, setImageList] = React.useState<string[]>([]);
  // 조회·셔플·실패 처리는 utils/cardNews 가 맡는다 (케어 MainNews 와 같은 규칙).
  // 예전에는 setLoading(false) 가 하위 요청을 띄운 직후 실행돼, 카드가 도착하기도 전에
  // 스켈레톤이 사라졌다.
  const { items: contentList, loading, error, reload } = useCardNews(NEWS_COUNT);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const handleOpen = (images: any) => {
    setImageList(images);
    setOpen(true);
  }
  const handleClose = () => setOpen(false);

  return (
    <React.Fragment>

      {/* 맞춤플랜 — 디지털트윈 화면 2 와 같은 것을 보여줘야 해서 컴포넌트로 분리했다 */}
      <MainCarePlan />

      <Box sx={{ width: '100%', mt: 3 }}>
        <Stack direction={'row'} alignItems={'center'}>
          <Typography sx={{ fontWeight: 700, color: '#777', fontSize: 20 }}>건강뉴스</Typography>
          <Typography sx={{ ml: 'auto', fontSize: 14, }} onClick={() => navigate('../News/HealthNews')}>전체보기 <ChevronRightIcon fontSize='small' sx={{ verticalAlign: 'sub', color: '#555' }} /></Typography>
        </Stack>

        <Box sx={{ whiteSpace: 'nowrap' }}>

          {error ? (
            // 예전에는 실패해도 console.log 뿐이라 제목과 출처 사이가 말없이 비었다
            <Stack alignItems={'center'} sx={{ py: 3 }}>
              <Typography sx={{ fontSize: 14, color: '#888' }}>건강뉴스를 불러오지 못했습니다.</Typography>
              <Button onClick={reload} sx={{ color: '#EF8322', fontSize: 14, fontWeight: 700 }}>다시 시도</Button>
            </Stack>
          ) : !loading && contentList.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: '#888', textAlign: 'center', py: 3 }}>
              등록된 건강뉴스가 없습니다.
            </Typography>
          ) : (
          <ImageList sx={{ overflowX: 'scroll', overflowY: 'hidden', display: 'block', pb: 1 }}>
            {loading ? (
              Array.from({ length: NEWS_COUNT }, (_unused, index) => (
                <ImageListItem key={index} sx={NewsItemStyle}>
                  <Stack direction={'column'} sx={{ width: '100%' }} justifyContent='center' alignContent={'center'}>
                    <Skeleton animation="wave" variant="rounded" height={100} width={200} sx={{ borderRadius: 3, }} />
                  </Stack>
                  <Stack direction={'column'} sx={{ width: '100%' }} justifyContent='center' alignContent={'center'}>
                    <Skeleton animation="wave" variant="rounded" height={21} width='100%' sx={{ borderRadius: 3, my: 1, mx: 'auto' }} />
                    <Skeleton animation="wave" variant="rounded" height={21} width='100%' sx={{ borderRadius: 3, }} />
                  </Stack>
                </ImageListItem>
              ))
            ) : (
            // 목록은 받은 뒤 한 번만 정해지고 다시 정렬되지 않으므로 index 키로 충분하다
            contentList.map((item, index) => (
              <ImageListItem key={index} sx={NewsItemStyle} onClick={() => handleOpen(item.images ?? [])}>

                <img
                  src={cardNewsImageUrl(item.thumbNail)}
                  alt={item.title}
                />

                <Box>
                  <Typography
                    sx={{ textAlign: 'center', textOverflow: 'ellipsis', overflow: 'hidden', my: 1, fontSize: 14, fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Stack direction={'row'} spacing={1} overflow={'scroll'}>
                    {/* hashTags 가 없는 항목이 하나라도 오면 화면 전체가 렌더에 실패했다 */}
                    {(item.hashTags ?? []).map((tag, tagIndex) => (
                      <Chip key={tagIndex} size="small" label={tag} />
                    ))}
                  </Stack>
                </Box>

              </ImageListItem>
            ))
            )}
          </ImageList>
          )}

        </Box>
        {loading ? (
          <Skeleton animation="wave" variant="rounded" height={21} width='80%' sx={{ borderRadius: 3, ml: 'auto' }} />
        ) : (
          <Typography sx={{ textAlign: 'right', fontSize: 11, color: '#aaa' }}>출처 : 듀얼헬스케어,<a href="https://blog.naver.com/dualhealth_official" title="Dualheathcare" target='_blank' style={{ color: '#aaa', textDecoration: 'none' }}> https://blog.naver.com/dualhealth_official</a></Typography>
        )}
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Slider {...settings}>
            {imageList && imageList.map((item, index) => (
              <Box key={index}>
                <Img alt="" src={cardNewsImageUrl(item)} sx={{}} />
              </Box>
            ))}
          </Slider>
        </Box>
      </Modal>

    </React.Fragment>
  );
}

/** 카드 한 장 — 로딩 스켈레톤도 같은 칸을 써야 자리가 흔들리지 않는다 */
const NewsItemStyle = {
  width: 200, height: 100, display: 'inline-flex', mr: '20px',
  '& .MuiImageListItem-img': { height: 100, borderRadius: 4 }
};

const BtnStyle = {
  display: 'flex',
  justifyContent: 'center',
  m: '3 0',
  mt: 5,
  boxSizing: 'border-box',
  pb: 10
};

const BottomBtnStyle = {
  width: '100%',
  bgcolor: '#EF8322',
  fontSize: 18,
  fontWeight: 700,
  borderRadius: '30px', p: 1,
  boxSizing: 'border-box',
  color: '#fff',
  border: '1px solid #ef8322',
  ':hover': {
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
    boxShadow: 'none',
    color: '#fff'
  },
  ':active': {
    boxShadow: 'none',
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
  },
  ':focus': {
    boxShadow: 'none',
  },
};

export default MainCare;

